import { Navigate, useRoutes } from 'react-router-dom';

// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';

import ResetPassword from './pages/auth/ResetPassword';

import Users from './pages/Users';
import OnBoardUser from './pages/onboarding/OnboardUser';
import Page404 from './pages/Page404';
import YourEdifiers from './pages/Teacher/YourEdifiers';
import TeacherDashboard from './pages/Teacher/Dashboard';
import EdifierDashboard from './pages/Edifier/Dashboard';
import AdminDashboard from './pages/admin/Dashboard';
import GoalsDetails from './pages/admin/GoalsDetails';
import MeetingDetails from './pages/admin/MeetingDetails';
import SurveyResponses from './pages/SurveyResponses';

import OnboardingAdOns from './pages/onboarding/OnBoardingAdOns';
import SendAdminNotifications from './pages/admin/SendNotifications';
import AppNotifications from './pages/AppNotifications';



import RoleProfile from './pages/AssociatedRoleProfile';
import MyAvailability from './pages/MyAvailability';
import MeetingInvites from './pages/MeetingInvites';
import UpcomingEvents from './pages/UpcomingEvents';
import Teachers from './pages/Edifier/YourTeachers';
import VerifyUser from './pages/auth/Verify';
import OnBoard from './pages/onboarding/OnBoarding';
import LoginPage from './pages/auth/LoginPage';
import Schools from './pages/admin/Schools';
import TermsAndConditions from './pages/auth/TermsAndConditions';
import VerifyDocuSign from './pages/VerifyDocuSign';
import Settings from './pages/Settings';

// admin pages
import SiteLeaderRequests from './pages/admin/AdminRequests';
import ManageCredentials from './pages/admin/ManageCredentials';
import ManagePrincipals from './pages/admin/ManagePrincipals';
import ManagePairings from './pages/admin/ManagePairings';
import QuoteManagement from './pages/admin/QuoteManagement';
import SchoolStats from './pages/admin/SchoolStats';
import AuditLogs from './pages/admin/AuditLogs';
import AdminChat from './pages/admin/AdminChat';

// Principal pages
import PrincipalDashboard from './pages/principal/PrincipalDashboard';
import PrincipalUsers from './pages/principal/PrincipalUsers';
import PrincipalPairings from './pages/principal/PrincipalPairings';
import SiteLeaderDashboard from './pages/SiteLeader/SiteLeaderDashboard';
import HelpCenter from './pages/HelpCenter';
import HelpArticleDetail from './pages/HelpArticleDetail';
import FAQs from './pages/FAQs';
import { getUserDetails } from './utils';
import { ROLES } from './constants';

// Dashboard wrapper component
function DashboardWrapper() {
  const userDetails = getUserDetails();
  const userRole = userDetails?.role;
  if (userRole === ROLES.ADMIN || userRole === ROLES.PRINCIPAL) {
    return <AdminDashboard />;
  }
  if (userRole === ROLES.SITE_LEADER) {
    return <SiteLeaderDashboard />;
  }
  if (userRole === ROLES.EDIFIER) {
    return <EdifierDashboard />;
  }
  if (userRole === ROLES.TEACHER) {
    return <TeacherDashboard />;
  }
}

// ----------------------------------------------------------------------

export default function Router() {
  // Get userDetails on each render to reflect latest localStorage changes
  const userDetails = getUserDetails();

  const isAdmin = userDetails && userDetails.role === ROLES.ADMIN;
  const isSiteLeader = userDetails && userDetails.role === ROLES.SITE_LEADER;
  const isPrincipal = userDetails && userDetails.role === ROLES.PRINCIPAL;
  const isTeacher = userDetails && userDetails.role === ROLES.TEACHER;
  const isAdminOrPrincipal = isAdmin || isPrincipal;
  const isAdminOrPrincipalOrSiteLeader = isAdmin || isPrincipal || isSiteLeader;

  const routes = useRoutes([
    {
      path: '/',
      element: userDetails ? <Navigate to="/dashboard/my-profile" /> : <Navigate to="/login" />,
    },
    {
      path: '/dashboard',

      element: userDetails?.role ? <DashboardLayout /> : <Navigate to="/login" />,
      children: [
        {
          element: isPrincipal ? <Navigate to="/dashboard/principal/dashboard" /> : <Navigate to="/dashboard/app" />,
          index: true,
        },
        { path: 'app', element: <DashboardWrapper /> },
        { path: 'paired', element: <YourEdifiers /> },

        {
          path: 'find-edifiers',
          element: isTeacher || isAdminOrPrincipal ? <Users /> : <Navigate to="/dashboard/teachers" />,
        },

        {
          path: 'district-users',
          element: isSiteLeader ? <Users /> : <Navigate to="/dashboard/user-profile" />,
        },
        { path: 'find-schools', element: isAdminOrPrincipal ? <Schools /> : <Navigate to="/dashboard/my-profile" /> },
        { path: 'requests', element: isAdminOrPrincipal ? <SiteLeaderRequests /> : <Navigate to="/dashboard/my-profile" /> },

        { path: 'my-profile', element: <RoleProfile /> },

        { path: 'user-profile', element: <RoleProfile /> },
        { path: 'meeting-invites', element: <MeetingInvites /> },
        { path: 'upcoming-events', element: <UpcomingEvents /> },

        { path: 'teachers', element: isTeacher ? <Navigate to="/dashboard/find-edifiers" /> : <Teachers /> },

        { path: 'notifications', element: <AppNotifications /> },

        { path: 'settings', element: <Settings /> },

        // OnboardingAdOns

        {
          path: 'timeslots',
          element: <MyAvailability />,
        },

        {
          path: 'user-timeslots',
          element: <MyAvailability isEdifier />,
        },
        {
          path: 'manage-credentials',
          element: isAdminOrPrincipal ? <ManageCredentials /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'manage-principals',
          element: isAdmin ? <ManagePrincipals /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'manage-pairings',
          element: isAdminOrPrincipalOrSiteLeader ? <ManagePairings /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'quote-management',
          element: isAdminOrPrincipal ? <QuoteManagement /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'school-stats/:schoolId',
          element: isAdminOrPrincipal ? <SiteLeaderDashboard /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'send-notifications',
          element: isAdmin ? <SendAdminNotifications /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'goals-details',
          element: isAdminOrPrincipalOrSiteLeader ? <GoalsDetails /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'meeting-details',
          element: isAdminOrPrincipalOrSiteLeader ? <MeetingDetails /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'survey-responses',
          element: isAdminOrPrincipal ? <SurveyResponses /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'audit-logs',
          element: isAdmin ? <AuditLogs /> : <Navigate to="/dashboard/my-profile" />,
        },

        {
          path: 'admin-chat',
          element: isAdmin ? <AdminChat /> : <Navigate to="/dashboard/my-profile" />,
        },

        // Principal routes
        {
          path: 'principal/dashboard',
          element: isPrincipal ? <PrincipalDashboard /> : <Navigate to="/dashboard/my-profile" />,
        },
        {
          path: 'principal/users',
          element: isPrincipal ? <PrincipalUsers /> : <Navigate to="/dashboard/my-profile" />,
        },
        {
          path: 'principal/pairings',
          element: isPrincipal ? <PrincipalPairings /> : <Navigate to="/dashboard/my-profile" />,
        },

        // {
        //   path: 'surveys',
        //   element: <Surveys />,
        // },
      ],
    },
    {
      path: 'login',
      element: userDetails ?  <Navigate to="/dashboard/my-profile" /> : <LoginPage />,
    },
    {
      path: 'verify-docusign',
      element: !userDetails ? <VerifyDocuSign /> : <Navigate to="/dashboard/my-profile" />,
    },
    { path: 'addons', element: <OnboardingAdOns /> },

    {
      path: 'onboard-user',
      element: userDetails ? <Navigate to="/dashboard/my-profile" /> : <OnBoardUser />,
    },
    {
      path: 'verify',
      element: userDetails ? <Navigate to="/dashboard/my-profile" /> : <VerifyUser />,
    },

    {
      path: 'reset-password',
      element: userDetails ? <Navigate to="/dashboard/my-profile" /> : <ResetPassword />,
    },
    {
      path: 'onboard',
      element: userDetails ? <Navigate to="/dashboard/my-profile" /> : <OnBoard />,
    },
    {
      path: 'terms-and-conditions', // New route for Terms and Conditions
      element: <TermsAndConditions />,
    },
    // Help Center routes (outside dashboard layout, no sidebar)
    {
      path: 'help',
      element: <SimpleLayout />,
      children: [
        { index: true, element: <HelpCenter /> },
        { path: ':slug', element: <HelpArticleDetail /> },
      ],
    },
    // FAQs route
    {
      path: 'faqs',
      element: <SimpleLayout />,
      children: [
        { index: true, element: <FAQs /> },
      ],
    },
    {
      element: <SimpleLayout />,
      children: [
        {
          element: isSiteLeader ? <Navigate to="/dashboard/district-users" /> : <Navigate to="/dashboard/app" />,
          index: true,
        },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
